import useFeature from '@core/components/LaunchDarkly';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { useAutoStartMicStream } from '@core/components/MicrophoneStreamProvider/useAutoStartMicStream';
import VoiceFocusedAvatar from '@core/components/VirtualHumans/VoiceFocusedAvatar/VoiceFocusedAvatar';
import LegacyVHInfoPopup from '@vh/components/VHInfoPopup/LegacyVHInfoPopup';
import VHInfoPopup from '@vh/components/VHInfoPopup/VHInfoPopup';
import { RealtimeOpenAIContext } from '@vh/components/VHPlayer/RealtimeOpenAIPlayer/RealtimeOpenAI.context';
import { setLoadingUserResponse } from '@vh/slices/CallSlice';
import {
  addBotChat,
  addUserChat,
  appendChatText,
  assessmentIdSelector,
  ChatStates,
  createRealtimeOpenAISession,
  forceStartSession,
  isPreviewPlayerSelector,
  manuallySubmitChatLog,
  selectChatState,
  selectTextChats,
  updateChat,
} from '@vh/slices/chat/ChatSlice';
import { patientSelector } from '@vh/slices/PatientSlice';
import {
  SilverstripeEndpointSelector,
  startAnalyticsSession,
} from '@vh/slices/SilverstripeAPISlice';
import {
  setMoreInfoVisible,
  userSelectedLocaleSelector,
} from '@vh/slices/vh-player/VHPlayerSlice';
import { virtualHumanDataSelector } from '@vh/slices/VirtualHumanSlice';
import { useEffectOnlyOnce } from '@vh/utility/EventHooks';
import { usePrevious } from '@vh/utility/StateHooks';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

/** @typedef {import('@vh/slices/chat/ChatSlice.types').ChatLogItem} ChatLogItem */

/** @type {React.FC<{vhId: number; onLoaded: () => void;}>} */
const RealtimeOpenAIPlayer = ({ vhId, onLoaded }) => {
  /** @type {React.MutableRefObject<HTMLAudioElement | null>} */
  const audioRef = useRef(null);
  useAutoStartMicStream(true);
  const { stream: micStream } = useContext(MicrophoneStreamContext);

  const dispatch = useDispatch();
  const chats = useSelector(selectTextChats);
  const { Voice } = useSelector(virtualHumanDataSelector);
  const vhDBVH = useSelector(patientSelector);

  /** @type {React.MutableRefObject<ChatLogItem[] | null>} */
  const chatsRef = useRef(null);
  chatsRef.current = chats;
  // const [inProgressUserChatId, setInProgressUserChatId] = useState(null);
  // const [inProgressBotChatId, setInProgressBotChatId] = useState(null);

  const onUserTranscriptComplete = useCallback(
    (itemId, transcript) => {
      console.log('updating user chat id ' + itemId);
      console.info({
        id: itemId,
        text: transcript,
        loading: false,
      });
      dispatch(
        updateChat({
          id: itemId,
          text: transcript,
          loading: false,
        })
      );
      const chat = chatsRef.current?.find((chat) => chat.id === itemId);
      const createdAtOverride = chat?.createdAt;
      dispatch(
        manuallySubmitChatLog({ userText: transcript, createdAtOverride })
      );
      dispatch(setLoadingUserResponse(false));
    },
    [dispatch]
  );

  const { startSession, stopSession, setupEvents, connected, sendText } =
    useContext(RealtimeOpenAIContext);

  const onOpenAISessionCreated = useCallback(
    (session) => {
      console.log('session created');
      console.log(session);
      if (!vhDBVH.skipGreeting && !!vhDBVH?.openingLine) {
        sendText('__INITIAL__');
      }
    },
    [sendText, vhDBVH?.openingLine, vhDBVH.skipGreeting]
  );

  const onOpenAITranscriptStarted = useCallback(
    (itemId) => {
      dispatch(addBotChat({ id: itemId, text: '', loading: true }));
      console.log('create bot chat id w ' + itemId);
      console.info({ id: itemId, text: '', loading: true });
    },
    [dispatch]
  );

  const onOpenAITranscriptPartAdded = useCallback(
    (itemId, part) => {
      if (!itemId) return;
      dispatch(
        appendChatText({
          id: itemId,
          text: part,
        })
      );
    },
    [dispatch]
  );

  const onOpenAITranscriptDone = useCallback(
    (itemId, transcript) => {
      console.log('updating bot id ' + itemId);
      console.info({
        id: itemId,
        text: transcript,
        loading: false,
      });
      dispatch(
        updateChat({
          id: itemId,
          // text: transcript,
          loading: false,
        })
      );
      const chat = chatsRef.current?.find((chat) => chat.id === itemId);
      const createdAtOverride = chat?.createdAt;
      dispatch(
        manuallySubmitChatLog({ botText: transcript, createdAtOverride })
      );
    },
    [dispatch]
  );

  const onUserSpeechStarted = useCallback(
    async (itemId) => {
      console.log('user text started');
      dispatch(setLoadingUserResponse(true));
      dispatch(addUserChat({ id: itemId, text: '', loading: true }));
      console.log('create user chat id ' + itemId);
    },
    [dispatch]
  );

  const onUserTextRequestComplete = useCallback(
    (itemId, text) => {
      if (text === '__INITIAL__') return;
      dispatch(addUserChat({ id: itemId, text, loading: false }));
      dispatch(manuallySubmitChatLog({ userText: text }));
    },
    [dispatch]
  );

  const realtimeOpenAIOptions = useMemo(() => {
    return {
      onUserTranscriptComplete,
      onOpenAITranscriptPartAdded,
      onOpenAITranscriptDone,
      onUserSpeechStarted,
      vhId,
      onOpenAITranscriptStarted,
      onUserTextRequestComplete,
      onOpenAISessionCreated,
    };
  }, [
    onOpenAITranscriptDone,
    onOpenAITranscriptPartAdded,
    onUserTranscriptComplete,
    vhId,
    onUserSpeechStarted,
    onOpenAITranscriptStarted,
    onUserTextRequestComplete,
    onOpenAISessionCreated,
  ]);

  const ssEndpoint = useSelector(SilverstripeEndpointSelector);
  const isPreviewPlayer = useSelector(isPreviewPlayerSelector);

  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (onLoaded && connected && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      onLoaded();
    }
  }, [connected, onLoaded, sendText]);

  // Start session analytics submission to Silverstripe!
  const assessmentId = useSelector(assessmentIdSelector);
  const previousAssessmentId = usePrevious(assessmentId);
  useEffect(() => {
    if (
      !isPreviewPlayer &&
      !!assessmentId &&
      assessmentId !== previousAssessmentId
    ) {
      dispatch(startAnalyticsSession(ssEndpoint));
    }
  }, [
    assessmentId,
    dispatch,
    isPreviewPlayer,
    previousAssessmentId,
    ssEndpoint,
  ]);

  const userSelectedLocale = useSelector(userSelectedLocaleSelector);

  const handleOnBeforeStart = useCallback(async () => {
    dispatch(createRealtimeOpenAISession({ vhId }));
    const sessionResponse = await startSession(
      audioRef,
      micStream,
      Voice,
      userSelectedLocale?.BCP47Code
    );
    setupEvents(realtimeOpenAIOptions);
    dispatch(forceStartSession(undefined));
  }, [
    dispatch,
    vhId,
    startSession,
    micStream,
    Voice,
    userSelectedLocale?.BCP47Code,
    setupEvents,
    realtimeOpenAIOptions,
  ]);

  useEffectOnlyOnce(() => {
    dispatch(setMoreInfoVisible(true));
  });

  const chatState = useSelector(selectChatState);

  useEffect(() => {
    if (chatState !== ChatStates.TALK) {
      stopSession();
    }
  }, [chatState, stopSession]);

  // TODO: reset state for ongoing requests on disconnect.
  // E.g. if there are in-progress transcriptions happening with loading: true chats

  const showSelectableUserLanguageFeature = useFeature(
    'show-selectable-user-language'
  );

  return (
    <div>
      {showSelectableUserLanguageFeature ? (
        <VHInfoPopup
          avatarComponent={<VoiceFocusedAvatar name={vhDBVH?.name} />}
          onBeforeStart={handleOnBeforeStart}
          vhId={vhId}
        />
      ) : (
        <LegacyVHInfoPopup
          avatarComponent={<VoiceFocusedAvatar name={vhDBVH?.name} />}
          onBeforeStart={handleOnBeforeStart}
          vhId={vhId}
        />
      )}
      <audio controls={false} ref={audioRef} />
    </div>
  );
};

export default RealtimeOpenAIPlayer;
